import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';

import {
    OrganisationDocument,
    IFeature,
    CurrentUserDocument,
    IRole,
} from 'graphql/types';

const CAN_UPGRADE_ROLES = [IRole.Owner];
const CAN_SEE_UPGRADE_ROLES = [IRole.Owner, IRole.Manager];

/**
 * The useFeature hook can be used to check if a certain feature is enabled for the organisation. You can also use
 * this to check if a user can see the upgrade page and/or can actually upgrade plan
 * @param feature The feature to check. If not provided, the hook will only return fixed values.
 * @param withSettings Whether to also fetch organisation settings.
 */
export const useFeature = (feature?: IFeature, withSettings?: boolean) => {
    const [
        getOrganisation,
        {
            data: organisationData,
            called: calledOrganisation,
            loading: loadingOrganisation,
        },
    ] = useLazyQuery(OrganisationDocument);
    const [
        getCurrentUser,
        { data: userData, called: calledUser, loading: loadingUser },
    ] = useLazyQuery(CurrentUserDocument);

    const {
        showChat = false,
        showOffers = false,
        showPortfolio = false,
        showSkills = false,
        showLibrary = false,
    } = organisationData?.organisation || {};

    const called = calledOrganisation || calledUser;
    const loading = loadingOrganisation || loadingUser;
    const userRoles = userData?.currentUser?.roles || [];

    const isManager = userRoles.includes(IRole.Manager);
    const showOffersSetting = showOffers || isManager;
    const showPortfolioSetting = showPortfolio || isManager;

    useEffect(() => {
        // When we don't have a feature we don't need to get the organisation and user
        if (!feature && !withSettings) return;

        getOrganisation();
        getCurrentUser();
    }, [feature, withSettings, getOrganisation, getCurrentUser]);

    const settings = withSettings
        ? {
              showChat,
              showOffers: showOffersSetting,
              showPortfolio: showPortfolioSetting,
              showSkills,
              showLibrary,
          }
        : {};

    // When no feature is given we want to return fixed values
    if (!feature) {
        return {
            canUse: true,
            userCanSeeUpgrade: false,
            userCanUpgrade: false,
            ...settings,
            loading: withSettings ? loading : false,
        };
    }

    const organisationFeatures = organisationData?.organisation?.features || [];

    // User can see the upgrade page
    const userCanSeeUpgrade = CAN_SEE_UPGRADE_ROLES.some((role) =>
        userRoles.includes(role)
    );

    // User can upgrade organisation plan
    const userCanUpgrade = CAN_UPGRADE_ROLES.some((role) =>
        userRoles.includes(role)
    );

    // Check if organisation features contains the given feature.
    const canUse = organisationFeatures.includes(feature);

    return {
        canUse,
        userCanSeeUpgrade,
        userCanUpgrade,
        ...settings,
        loading: loading || !called,
    };
};
